import React, { useState, useEffect } from 'react';
import EventBookingCard from '../components/EventBookingCard';
import { useAuth } from '../App';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { 
  User, 
  EnvelopeSimple, 
  Phone,
  CreditCard,
  Medal,
  Ticket,
  CalendarBlank,
  Clock,
  Check,
  Trophy,
  LockKey
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import MemberBadge from '../components/MemberBadge';
import { getLevelLabel, getLevelBadgeClass, LEVEL_OPTIONS } from '../utils/level';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PadlrRating from '../components/PadlrRating';
import { toast } from 'sonner';
import { CompetitiveResultForm } from '../components/CompetitiveResultForm';
import { HeadCoachStats } from '../components/HeadCoachStats';

const ProfilePage = () => {
  const { user, t, language, setLanguage, API, checkAuth } = useAuth();
  const navigate = useNavigate();
  const dateLocale = language === 'de' ? de : enUS;

  const [profile, setProfile] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    preferred_language: user?.preferred_language || 'en',
    player_level: String(user?.player_level || 4)
  });
  const [bookings, setBookings] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [bookingsView, setBookingsView] = useState('upcoming');
  const nowKey = format(new Date(), "yyyy-MM-dd'T'HH:mm");
  const bookingKey = (b, field = 'end_time') => `${b.date || ''}T${b[field] || b.start_time || '00:00'}`;
  const allEntries = [...bookings, ...myEvents.map(e => ({ ...e, _event: true }))];
  const upcomingBookings = allEntries.filter(b => bookingKey(b) >= nowKey)
    .sort((a, b) => bookingKey(a, 'start_time').localeCompare(bookingKey(b, 'start_time')));
  const pastBookings = allEntries.filter(b => bookingKey(b) < nowKey)
    .sort((a, b) => bookingKey(b, 'start_time').localeCompare(bookingKey(a, 'start_time')));
  const shownBookings = bookingsView === 'past' ? pastBookings : upcomingBookings;
  const [payments, setPayments] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [matches, setMatches] = useState([]);
  const [resultDialog, setResultDialog] = useState(null); // {match, winners:[]}
  const [submittingResult, setSubmittingResult] = useState(false);
  const [ratingRefreshKey, setRatingRefreshKey] = useState(0);
  const [voucherCode, setVoucherCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPw, setChangingPw] = useState(false);

  // Deep-linkable active tab. `/profile?tab=bookings` lands straight in the
  // Bookings tab (e.g. from the dashboard "My Bookings" card or an email link).
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const validTabs = ['profile', 'rating', 'matches', 'bookings', 'credits', 'payments'];
  const [activeTab, setActiveTabState] = useState(
    validTabs.includes(initialTab) ? initialTab : 'profile'
  );
  const setActiveTab = (v) => {
    setActiveTabState(v);
    const next = new URLSearchParams(searchParams);
    if (v === 'profile') next.delete('tab');
    else next.set('tab', v);
    setSearchParams(next, { replace: true });
  };
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && validTabs.includes(t) && t !== activeTab) setActiveTabState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [cancelConfirm, setCancelConfirm] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [bookingsRes, paymentsRes, vouchersRes, subsRes, matchesRes] = await Promise.all([
        API.get('/bookings'),
        API.get('/payments'),
        API.get('/vouchers/my').catch(() => ({ data: [] })),
        API.get('/subscriptions/my').catch(() => ({ data: [] })),
        API.get('/matches?my_matches=true').catch(() => ({ data: [] })),
      ]);
      API.get('/my/events').then(r => setMyEvents(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      setBookings(bookingsRes.data);
      setPayments(paymentsRes.data);
      setVouchers(Array.isArray(vouchersRes.data) ? vouchersRes.data : []);
      setSubscriptions(Array.isArray(subsRes.data) ? subsRes.data : []);
      setMatches(Array.isArray(matchesRes.data) ? matchesRes.data : []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const payload = { name: profile.name, phone: profile.phone, preferred_language: profile.preferred_language };
      if (user?.role === 'admin' || user?.role === 'staff') {
        payload.player_level = parseFloat(profile.player_level);
      }
      await API.put('/users/me', payload);
      if (profile.preferred_language !== language) {
        setLanguage(profile.preferred_language);
      }
      await checkAuth();
      toast.success(language === 'de' ? 'Profil aktualisiert!' : 'Profile updated!');
    } catch (error) {
      toast.error(language === 'de' ? 'Fehler beim Speichern' : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleRedeemVoucher = async () => {
    if (!voucherCode.trim()) return;
    
    try {
      setRedeeming(true);
      const { data } = await API.post('/vouchers/redeem', { code: voucherCode });
      toast.success(data.message);
      setVoucherCode('');
      await checkAuth();
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'de' ? 'Ungültiger Gutschein' : 'Invalid voucher');
      toast.error(msg);
    } finally {
      setRedeeming(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    setCancelConfirm({
      bookingId,
      onConfirm: async () => {
        try {
          const { data } = await API.put(`/bookings/${bookingId}/cancel`);
          if (data?.refund?.amount) {
            toast.success(language === 'de'
              ? `Buchung storniert — ${data.refund.amount.toFixed(2)} CHF Rückerstattung ist unterwegs (1–3 Werktage).`
              : `Booking cancelled — ${data.refund.amount.toFixed(2)} CHF refund on its way (1–3 business days).`);
          } else {
            toast.success(language === 'de' ? 'Buchung storniert' : 'Booking cancelled');
          }
          loadData();
          await checkAuth();
        } catch (error) {
          const msg = error.response?.data?.detail || (language === 'de' ? 'Stornierung fehlgeschlagen' : 'Cancellation failed');
          toast.error(msg);
        }
        setCancelConfirm(null);
      }
    });
  };

  const canCancelBooking = (booking) => {
    if (booking.status === 'cancelled') return false;
    const bookingDate = new Date(booking.date + 'T' + booking.start_time);
    const now = new Date();
    const hoursUntil = (bookingDate - now) / (1000 * 60 * 60);
    return hoursUntil >= 24;
  };

  const getHoursUntilBooking = (booking) => {
    const bookingDate = new Date(booking.date + 'T' + booking.start_time);
    const now = new Date();
    return Math.round((bookingDate - now) / (1000 * 60 * 60));
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      toast.error(language === 'de' ? 'Passwort muss mindestens 6 Zeichen lang sein' : 'Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(language === 'de' ? 'Passwörter stimmen nicht überein' : 'Passwords do not match');
      return;
    }
    try {
      setChangingPw(true);
      await API.put('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
      toast.success(language === 'de' ? 'Passwort geändert!' : 'Password changed!');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'de' ? 'Fehler beim Ändern' : 'Failed to change password'));
    } finally { setChangingPw(false); }
  };

  const getLevelBadge = (level) => getLevelBadgeClass(level);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2 flex items-center gap-2">
          {t('myProfile')}
          <MemberBadge user={user} size={26} />
        </h1>
        <p className="text-slate-500">
          {user?.is_member
            ? (language === 'de'
                ? `Mitglied bis ${user?.member_until || '—'}`
                : `Member until ${user?.member_until || '—'}`)
            : (language === 'de'
                ? 'Verwalten Sie Ihr Konto und Ihre Buchungen'
                : 'Manage your account and bookings')}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="stat-card"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="label-text">{t('credits')}</span>
            <CreditCard size={20} className="text-emerald-600" weight="duotone" />
          </div>
          <p className="text-4xl font-black text-emerald-600">{user?.credits_balance?.toFixed(0)}</p>
          <p className="text-slate-500 text-sm">CHF</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="stat-card cursor-pointer hover:shadow-md transition"
          onClick={() => setActiveTab('rating')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveTab('rating'); }}
          data-testid="profile-level-card"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="label-text">{t('playerLevel')}</span>
            <Medal size={20} className="text-blue-600" weight="duotone" />
          </div>
          <div className="flex items-center gap-3">
            <p className={`level-badge ${getLevelBadge(user?.player_level)} text-4xl font-black !px-4 !py-2`}>{user?.player_level}</p>
            <span className="text-xs text-emerald-600 font-semibold">{language === 'de' ? 'Verlauf ansehen' : 'View history'} →</span>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="stat-card"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="label-text">{t('myBookings')}</span>
            <CalendarBlank size={20} className="text-green-600" weight="duotone" />
          </div>
          <p className="text-4xl font-black text-slate-900">{bookings.filter(b => b.status !== 'cancelled').length}</p>
          <p className="text-slate-500 text-sm">{language === 'de' ? 'Gesamt' : 'Total'}</p>
        </motion.div>
      </div>

      {user?.is_head_coach && (
        <HeadCoachStats API={API} L={(en, de) => (language === 'de' ? de : en)} />
      )}

      {/* Tabs — support `?tab=bookings` deep-link so dashboard/email links
          can jump straight into the customer's bookings list. */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 w-full grid grid-cols-3 sm:grid-cols-6 h-auto gap-1 p-1">
          <TabsTrigger value="profile" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-900 text-xs sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap">
            {language === 'de' ? 'Profil' : 'Profile'}
          </TabsTrigger>
          <TabsTrigger value="rating" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-900 text-xs sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap" data-testid="profile-tab-rating">
            {language === 'de' ? 'Rating' : 'Rating'}
          </TabsTrigger>
          <TabsTrigger value="matches" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-900 text-xs sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap" data-testid="profile-tab-matches">
            {language === 'de' ? 'Matches' : 'Matches'} ({matches.length})
          </TabsTrigger>
          <TabsTrigger value="bookings" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-900 text-xs sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap" data-testid="profile-tab-bookings">
            {t('bookings')}
          </TabsTrigger>
          <TabsTrigger value="credits" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-900 text-xs sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap" data-testid="profile-tab-credits">
            <span className="flex items-center gap-1.5">
              {t('credits')}
              {(() => {
                const activeSubs = subscriptions.filter(s => (s.lessons_remaining ?? Math.max(0, (s.total_lessons || 0) - (s.used_lessons || 0))) > 0 || !!s.is_yearly).length;
                const activeVouchers = vouchers.filter(v => v.is_active && (v.remaining_balance ?? 0) > 0.01).length;
                const total = activeSubs + activeVouchers;
                if (!total) return null;
                return (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold" data-testid="profile-tab-credits-count">
                    {total}
                  </span>
                );
              })()}
            </span>
          </TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-900 text-xs sm:text-sm px-2 sm:px-3 py-2 whitespace-nowrap">
            {t('payments')}
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-6">
          <div className="card-static p-6 max-w-xl">
            <h3 className="text-lg font-bold text-slate-900 mb-6">
              {language === 'de' ? 'Persönliche Daten' : 'Personal Information'}
            </h3>
            
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="label-text">{t('name')}</Label>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    value={profile.name}
                    onChange={(e) => setProfile({...profile, name: e.target.value})}
                    className="pl-10 bg-slate-50 border-slate-200 text-slate-900"
                    data-testid="profile-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="label-text">{t('email')}</Label>
                <div className="relative">
                  <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    value={user?.email}
                    disabled
                    className="pl-10 bg-slate-50 border-slate-200 text-slate-500 cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="label-text">{t('phone')}</Label>
                <div className="relative">
                  <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    value={profile.phone}
                    onChange={(e) => setProfile({...profile, phone: e.target.value})}
                    className="pl-10 bg-slate-50 border-slate-200 text-slate-900"
                    placeholder="+41 79 123 45 67"
                    data-testid="profile-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="label-text">{t('playerLevel')} (1 = {language === 'de' ? 'Profi' : 'Pro'}, 7.9 = {language === 'de' ? 'Kompletter Anfänger' : 'Complete Beginner'})</Label>
                {(user?.role === 'admin' || user?.role === 'staff') ? (
                  <Select value={profile.player_level} onValueChange={(v) => setProfile({...profile, player_level: v})}>
                    <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-900" data-testid="profile-level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 max-h-60">
                      {LEVEL_OPTIONS.map(l => (
                        <SelectItem key={l} value={l.toString()}>
                          {l.toFixed(1)} — {getLevelLabel(l, language)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 flex items-center justify-between" data-testid="profile-level-readonly">
                    <span className="text-slate-900 font-semibold">{profile.player_level}</span>
                    <span className="text-[11px] text-slate-400 italic">
                      {language === 'de'
                        ? 'Nur Admin kann das Level ändern'
                        : 'Only an admin can change your level'}
                    </span>
                  </div>
                )}
                {!(user?.role === 'admin' || user?.role === 'staff') && (
                  <p className="text-[11px] text-slate-500">
                    {language === 'de'
                      ? 'Dein Level entwickelt sich automatisch durch Mixin-Turniere und kompetitive Spiele (siehe Rating-Tab).'
                      : 'Your level evolves automatically via Mixin tournaments and competitive matches (see the Rating tab).'}
                  </p>
                )}
                {user?.role === 'customer' && !user?.is_member && !user?.level_quiz_completed_at && (
                  <button
                    type="button"
                    onClick={() => navigate('/onboarding/level-quiz')}
                    data-testid="profile-open-level-quiz"
                    className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-emerald-700 hover:text-emerald-800 underline"
                  >
                    {language === 'de' ? '5-Fragen Level-Quiz starten →' : 'Take the 5-question level quiz →'}
                  </button>
                )}
              </div>

              <div className="space-y-2">
                <Label className="label-text">{language === 'de' ? 'Sprache' : 'Language'}</Label>
                <Select 
                  value={profile.preferred_language} 
                  onValueChange={(v) => setProfile({...profile, preferred_language: v})}
                >
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-50 border-slate-200">
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleSaveProfile}
                disabled={saving}
                className="btn-primary"
                data-testid="save-profile"
              >
                {saving ? t('loading') : t('save')}
              </Button>
            </div>
          </div>

          {/* Change Password */}
          <div className="card-static p-6 max-w-xl mt-6">
            <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
              <LockKey size={20} className="text-slate-600" weight="duotone" />
              {language === 'de' ? 'Passwort ändern' : 'Change Password'}
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="label-text">{language === 'de' ? 'Aktuelles Passwort' : 'Current Password'}</Label>
                <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-900" data-testid="current-password" />
              </div>
              <div className="space-y-2">
                <Label className="label-text">{language === 'de' ? 'Neues Passwort' : 'New Password'}</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-900" placeholder={language === 'de' ? 'Min. 6 Zeichen' : 'Min. 6 characters'} data-testid="new-password" />
              </div>
              <div className="space-y-2">
                <Label className="label-text">{language === 'de' ? 'Passwort bestätigen' : 'Confirm Password'}</Label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-900" data-testid="confirm-password" />
              </div>
              <Button onClick={handleChangePassword} disabled={changingPw || !currentPassword || !newPassword || !confirmPassword}
                className="bg-slate-800 text-white hover:bg-slate-900 font-bold" data-testid="change-password-btn">
                {changingPw ? '...' : (language === 'de' ? 'Passwort ändern' : 'Change Password')}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Padlr Rating Tab */}
        <TabsContent value="rating" className="mt-6">
          <PadlrRating refreshKey={ratingRefreshKey} />
        </TabsContent>

        {/* Matches Tab — every match the user played, past & future */}
        <TabsContent value="matches" className="mt-6">
          <div className="card-static p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-6">
              {language === 'de' ? 'Meine Matches' : 'My Matches'}
            </h3>
            {matches.length === 0 ? (
              <div className="text-center py-8">
                <Trophy size={48} className="text-slate-300 mx-auto mb-4" weight="duotone" />
                <p className="text-slate-500">
                  {language === 'de' ? 'Noch keine Matches gespielt' : "You haven't played any matches yet"}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {matches.map((m) => {
                  const now = new Date();
                  const matchDate = new Date(`${m.date}T${m.start_time}:00`);
                  const isPast = matchDate < now;
                  const isCompetitive = m.match_kind === 'competitive';
                  const players = m.current_players || [];
                  const isFull = players.length >= (m.max_players || 4);
                  // Result submission is available for BOTH competitive and
                  // friendly matches — the difference is only whether the
                  // Padlr rating updates (backend handles that).
                  const canSubmit = isFull && !m.result_recorded && (isPast || matchDate < new Date());
                  const winnerIds = m.winner_ids || [];
                  return (
                    <div
                      key={m.id}
                      className="bg-slate-50 border border-slate-200 p-4 rounded-lg"
                      data-testid={`profile-match-${m.id}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-bold text-slate-900 truncate">{m.court_name}</p>
                            {isCompetitive ? (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-amber-500 text-white">
                                🏆 {language === 'de' ? 'Wertungsmatch' : 'Rated'}
                              </span>
                            ) : (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-sky-100 text-sky-700">
                                🎾 {language === 'de' ? 'Freundschaftsspiel' : 'Friendly'}
                              </span>
                            )}
                            {m.result_recorded && (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`profile-result-badge-${m.id}`}>
                                ✓ {language === 'de' ? 'Resultat erfasst' : 'Result recorded'}{m.score_text ? ` · ${m.score_text}` : ''}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <CalendarBlank size={14} />
                              {format(new Date(m.date), 'PP', { locale: dateLocale })}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {m.start_time} - {m.end_time}
                            </span>
                          </div>
                          <div className="mt-2 text-xs text-slate-600">
                            <strong>{language === 'de' ? 'Spieler' : 'Players'}:</strong>{' '}
                            {players.map((p) => {
                              const won = winnerIds.includes(p.id);
                              return (
                                <span key={p.id} className={`inline-block mr-2 ${won ? 'text-emerald-700 font-semibold' : ''}`}>
                                  {won && '🏆 '}{p.name}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                      {canSubmit && (
                        <Button
                          onClick={() => setResultDialog({ match: m, winners: [] })}
                          size="sm"
                          className="mt-2 bg-amber-500 hover:bg-amber-600 text-white font-bold"
                          data-testid={`submit-result-btn-${m.id}`}
                        >
                          🏆 {language === 'de' ? 'Sieger eingeben' : 'Submit result'}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Bookings Tab */}
        <TabsContent value="bookings" className="mt-6">
          <div className="card-static p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <h3 className="text-lg font-bold text-slate-900">{t('myBookings')}</h3>
              <div className="inline-flex bg-slate-100 rounded-xl p-1 gap-1" data-testid="bookings-time-switch">
                {[['upcoming', language === 'de' ? 'Kommende' : 'Upcoming', upcomingBookings.length], ['past', language === 'de' ? 'Vergangene' : 'Past', pastBookings.length]].map(([k, lbl, n]) => (
                  <button key={k} type="button" onClick={() => setBookingsView(k)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${bookingsView === k ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    data-testid={`bookings-view-${k}`}>{lbl} <span className="text-xs opacity-60">({n})</span></button>
                ))}
              </div>
            </div>
            
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-lg" />
                ))}
              </div>
            ) : shownBookings.length === 0 ? (
              <div className="text-center py-8" data-testid="bookings-empty">
                <CalendarBlank size={48} className="text-slate-500 mx-auto mb-4" weight="duotone" />
                <p className="text-slate-500">
                  {bookingsView === 'past'
                    ? (language === 'de' ? 'Keine vergangenen Buchungen' : 'No past bookings')
                    : (language === 'de' ? 'Keine kommenden Buchungen' : 'No upcoming bookings')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Sort ascending by date+time so the NEXT reservation is on
                    top and the furthest away one at the bottom. */}
                {shownBookings.map((booking) => booking._event ? (
                  <EventBookingCard key={`${booking.kind}-${booking.id}`} ev={booking} language={language} dateLocale={dateLocale} />
                ) : (
                  <div 
                    key={booking.id}
                    className="bg-slate-50 border border-slate-200 p-4 rounded-lg"
                    data-testid={`profile-booking-${booking.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-slate-900 truncate">{booking.court_name}</p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <CalendarBlank size={14} />
                            {format(new Date(booking.date), 'PP', { locale: dateLocale })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {booking.start_time} - {booking.end_time}
                          </span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-emerald-600 font-bold">{booking.amount?.toFixed(0)} CHF</p>
                        <span className={`text-xs font-bold uppercase ${
                          booking.status === 'confirmed' 
                            ? 'text-green-600' 
                            : booking.status === 'cancelled'
                            ? 'text-red-500'
                            : 'text-amber-600'
                        }`}>
                          {booking.status}
                        </span>
                      </div>
                    </div>
                    {booking.status !== 'cancelled' && new Date(booking.date) >= new Date() && (
                      canCancelBooking(booking) ? (
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancelBooking(booking.id)}
                          className="mt-3 text-red-500 border-red-200 hover:bg-red-50"
                          data-testid={`cancel-booking-${booking.id}`}
                        >
                          {language === 'de' ? 'Stornieren' : 'Cancel'}
                        </Button>
                      ) : (
                        <p className="mt-2 text-xs text-slate-400">
                          {language === 'de' 
                            ? `Stornierung nicht mehr möglich (< 24h)` 
                            : `Cancellation no longer possible (< 24h)`}
                        </p>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Credits Tab */}
        <TabsContent value="credits" className="mt-6">
          {/* Summary strip at the top — one place to see everything the user owns:
              CHF credits, active Abos, remaining voucher balance. Each tile is a
              jump-link to the detail section below so clients find them fast. */}
          {(() => {
            const activeSubs = subscriptions.filter(s => (s.lessons_remaining ?? Math.max(0, (s.total_lessons || 0) - (s.used_lessons || 0))) > 0 || !!s.is_yearly);
            const activeVouchers = vouchers.filter(v => v.is_active && (Number(v.remaining_balance ?? 0) > 0.01));
            const voucherTotal = activeVouchers.reduce((sum, v) => sum + Number(v.remaining_balance || 0), 0);
            const scrollTo = (id) => {
              const el = document.getElementById(id);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            };
            return (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="credits-summary-strip">
                <button
                  type="button"
                  onClick={() => scrollTo('credits-balance-section')}
                  className="card-static p-4 text-left hover:ring-2 hover:ring-emerald-300 transition-all"
                  data-testid="credits-tile-balance"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
                      {language === 'de' ? 'Guthaben' : 'Credits'}
                    </span>
                    <CreditCard size={18} className="text-emerald-600" weight="duotone" />
                  </div>
                  <p className="text-3xl font-black text-emerald-600">{Number(user?.credits_balance || 0).toFixed(2)}</p>
                  <p className="text-xs text-slate-400 mt-0.5">CHF</p>
                </button>
                <button
                  type="button"
                  onClick={() => scrollTo('credits-abos-section')}
                  className="card-static p-4 text-left hover:ring-2 hover:ring-emerald-300 transition-all"
                  data-testid="credits-tile-abos"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
                      {language === 'de' ? 'Meine Abos' : 'My Subscriptions'}
                    </span>
                    <Ticket size={18} className="text-blue-600" weight="duotone" />
                  </div>
                  <p className="text-3xl font-black text-blue-600">{activeSubs.length}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {activeSubs.length === 0
                      ? (language === 'de' ? 'Keine aktiven Abos' : 'No active subscriptions')
                      : (language === 'de' ? 'aktiv' : 'active')}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => scrollTo('credits-vouchers-section')}
                  className="card-static p-4 text-left hover:ring-2 hover:ring-emerald-300 transition-all"
                  data-testid="credits-tile-vouchers"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
                      {language === 'de' ? 'Gutscheine' : 'Vouchers'}
                    </span>
                    <Ticket size={18} className="text-purple-600" weight="duotone" />
                  </div>
                  <p className="text-3xl font-black text-purple-600">{voucherTotal.toFixed(2)}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {activeVouchers.length === 0
                      ? (language === 'de' ? 'Keine aktiven Gutscheine' : 'No active vouchers')
                      : (language === 'de' ? `CHF über ${activeVouchers.length} ${activeVouchers.length === 1 ? 'Gutschein' : 'Gutscheine'}` : `CHF across ${activeVouchers.length} voucher${activeVouchers.length === 1 ? '' : 's'}`)}
                  </p>
                </button>
              </div>
            );
          })()}

          <div id="credits-balance-section" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card-static p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-6">
                {language === 'de' ? 'Aktuelles Guthaben' : 'Current Balance'}
              </h3>
              <div className="text-center py-8">
                <CreditCard size={48} className="text-emerald-600 mx-auto mb-4" weight="duotone" />
                <p className="text-5xl font-black text-emerald-600">{user?.credits_balance?.toFixed(2)}</p>
                <p className="text-slate-500 text-lg mt-2">CHF</p>
              </div>
            </div>

            <div className="card-static p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-6">{t('redeemVoucher')}</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-text">{t('voucherCode')}</Label>
                  <div className="relative">
                    <Ticket size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <Input
                      value={voucherCode}
                      onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                      className="pl-10 bg-slate-50 border-slate-200 text-slate-900 uppercase"
                      placeholder="XXXX-XXXX"
                      data-testid="voucher-code"
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleRedeemVoucher}
                  disabled={!voucherCode.trim() || redeeming}
                  className="w-full btn-primary"
                  data-testid="redeem-voucher"
                >
                  {redeeming ? t('loading') : t('redeemVoucher')}
                </Button>
              </div>
            </div>
          </div>

          {/* Subscriptions (Abos) */}
          {subscriptions.length > 0 && (
            <div id="credits-abos-section" className="card-static p-6 mt-6" data-testid="profile-subs-card">
              <h3 className="text-lg font-bold text-slate-900 mb-4">
                {language === 'de' ? 'Meine Abos' : 'My Subscriptions'}
              </h3>
              <div className="space-y-4">
                {subscriptions.map((s) => {
                  const isYearly = !!s.is_yearly;
                  const isCourt = s.type === 'court';
                  const isGolfGroup = s.type === 'golf_yearly_group';
                  const total = s.total_lessons || 0;
                  const used = s.used_lessons || 0;
                  const remaining = s.lessons_remaining ?? Math.max(0, total - used);
                  const pct = total > 0 ? Math.round((used / total) * 100) : 0;

                  // Yearly-specific info
                  const validUntil = s.valid_until ? new Date(s.valid_until) : null;
                  const now = new Date();
                  const daysLeft = validUntil ? Math.max(0, Math.ceil((validUntil - now) / (1000 * 60 * 60 * 24))) : null;
                  const expired = validUntil ? validUntil < now : false;

                  const yearlyLabel = isGolfGroup
                    ? (language === 'de' ? 'GOLF JAHR · MIT GÄSTEN' : 'GOLF YEAR · +GUESTS')
                    : (language === 'de' ? 'GOLF JAHR · SOLO' : 'GOLF YEAR · SOLO');

                  return (
                    <div key={s.id} className="border border-slate-200 rounded-xl p-4" data-testid={`profile-sub-${s.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-bold ${
                            isYearly ? 'bg-amber-100 text-amber-800' : (isCourt ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700')
                          }`}>
                            {isYearly ? yearlyLabel : (isCourt ? (language === 'de' ? 'COURT' : 'COURT') : 'TRAINING')}
                          </span>
                          <span className="text-sm text-slate-500">
                            {language === 'de' ? 'Gekauft' : 'Purchased'}: {s.purchased_at ? format(new Date(s.purchased_at), 'PP', { locale: dateLocale }) : '—'}
                          </span>
                        </div>
                        {isYearly ? (
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${expired ? 'bg-slate-100 text-slate-500' : (daysLeft <= 30 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700')}`}>
                            {expired
                              ? (language === 'de' ? 'Abgelaufen' : 'Expired')
                              : (language === 'de' ? `${daysLeft} Tage übrig` : `${daysLeft} days left`)}
                          </span>
                        ) : (
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${remaining > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                            {remaining} / {total} {isCourt ? (language === 'de' ? 'Std. übrig' : 'slots left') : (language === 'de' ? 'Lekt. übrig' : 'lessons left')}
                          </span>
                        )}
                      </div>

                      {/* Yearly-specific stats */}
                      {isYearly && (
                        <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
                            <p className="text-[11px] uppercase font-bold text-slate-500">
                              {language === 'de' ? 'Gültig bis' : 'Valid until'}
                            </p>
                            <p className="font-bold text-slate-900">
                              {validUntil ? format(validUntil, 'PP', { locale: dateLocale }) : '—'}
                            </p>
                          </div>
                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
                            <p className="text-[11px] uppercase font-bold text-slate-500">
                              {language === 'de' ? 'Bereits genutzt' : 'Sessions used'}
                            </p>
                            <p className="font-bold text-slate-900">
                              {used}× {language === 'de' ? '(unbegrenzt)' : '(unlimited)'}
                            </p>
                          </div>
                        </div>
                      )}

                      {!isYearly && (
                        <div className="w-full bg-slate-100 rounded-full h-2 mb-3 overflow-hidden">
                          <div
                            className={`h-full ${remaining > 0 ? 'bg-emerald-500' : 'bg-slate-400'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      )}

                      {(s.usage_log || []).length > 0 ? (
                        <details className="mt-2" open={isYearly && (s.usage_log || []).length <= 5}>
                          <summary className="cursor-pointer text-xs font-semibold text-slate-500 hover:text-slate-900">
                            {language === 'de' ? `Verlauf (${s.usage_log.length})` : `History (${s.usage_log.length})`}
                          </summary>
                          <ul className="mt-2 space-y-1 text-xs">
                            {s.usage_log.slice(0, 30).map((u, i) => (
                              <li key={i} className="flex items-center justify-between text-slate-600 border-b border-slate-100 last:border-b-0 py-1">
                                <span>
                                  {u.booking_date
                                    ? format(new Date(u.booking_date + 'T00:00:00'), 'PP', { locale: dateLocale })
                                    : (u.used_at ? format(new Date(u.used_at), 'PP', { locale: dateLocale }) : '—')}
                                  {u.booking_start_time ? ` · ${u.booking_start_time}` : ''}
                                  {u.booking_end_time ? `-${u.booking_end_time}` : ''}
                                  {u.booking_court_name ? ` · ${u.booking_court_name}` : ''}
                                  {u.booking_trainer_name ? ` · ${u.booking_trainer_name}` : ''}
                                </span>
                                <span className="font-mono text-slate-400">{isYearly ? '✓' : '−1'}</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      ) : (
                        <p className="text-xs text-slate-400 italic">
                          {language === 'de' ? 'Noch keine Nutzung' : 'Not used yet'}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Vouchers (Gutscheine) */}
          {vouchers.length > 0 && (
            <div id="credits-vouchers-section" className="card-static p-6 mt-6" data-testid="profile-vouchers-card">
              <h3 className="text-lg font-bold text-slate-900 mb-4">
                {language === 'de' ? 'Meine Gutscheine' : 'My Vouchers'}
              </h3>
              <div className="space-y-4">
                {vouchers.map((v) => {
                  const remaining = Number(v.remaining_balance ?? v.amount ?? 0);
                  const total = Number(v.amount ?? 0);
                  const used = Math.max(0, total - remaining);
                  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
                  const usageLog = v.usage_log || [];
                  const isCredit = v.voucher_type === 'credit';
                  const label = isCredit ? 'CHF' : (language === 'de' ? 'CHF Training' : 'CHF Training');
                  return (
                    <div key={v.id} className="border border-slate-200 rounded-xl p-4" data-testid={`profile-voucher-${v.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-bold ${isCredit ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                            {isCredit ? (language === 'de' ? 'GUTHABEN' : 'CREDIT') : 'TRAINING'}
                          </span>
                          <span className="font-mono text-xs text-slate-500">{v.code}</span>
                        </div>
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${remaining > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          {remaining.toFixed(2)} / {total.toFixed(2)} {label} {language === 'de' ? 'übrig' : 'left'}
                        </span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2 mb-3 overflow-hidden">
                        <div
                          className={`h-full ${remaining > 0 ? 'bg-emerald-500' : 'bg-slate-400'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      {v.description && (
                        <p className="text-xs text-slate-500 mb-2">{v.description}</p>
                      )}
                      {v.expires_at && (
                        <p className="text-[11px] text-slate-400 mb-2">
                          {language === 'de' ? 'Gültig bis' : 'Valid until'}: {format(new Date(v.expires_at), 'PP', { locale: dateLocale })}
                        </p>
                      )}
                      {usageLog.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs font-semibold text-slate-500 hover:text-slate-900">
                            {language === 'de' ? `Verwendungen (${usageLog.length})` : `Redemptions (${usageLog.length})`}
                          </summary>
                          <ul className="mt-2 space-y-1 text-xs">
                            {usageLog.slice(0, 20).map((u, i) => (
                              <li key={i} className="flex items-center justify-between text-slate-600 border-b border-slate-100 last:border-b-0 py-1">
                                <span>
                                  {u.used_at ? format(new Date(u.used_at), 'PPp', { locale: dateLocale }) : '—'}
                                  {u.description ? ` · ${u.description}` : ''}
                                </span>
                                <span className="font-mono text-red-600 font-semibold">
                                  −{Number(u.amount_used || 0).toFixed(2)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="mt-6">
          <div className="card-static p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-6">{t('payments')}</h3>
            
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-slate-100 animate-pulse rounded-lg" />
                ))}
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard size={48} className="text-slate-500 mx-auto mb-4" weight="duotone" />
                <p className="text-slate-500">
                  {language === 'de' ? 'Keine Zahlungen vorhanden' : 'No payments yet'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 label-text">{language === 'de' ? 'Datum' : 'Date'}</th>
                      <th className="text-left py-3 px-4 label-text">{language === 'de' ? 'Betrag' : 'Amount'}</th>
                      <th className="text-left py-3 px-4 label-text">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.id} className="border-b border-slate-100">
                        <td className="py-3 px-4 text-slate-900">
                          {format(new Date(payment.created_at), 'PP', { locale: dateLocale })}
                        </td>
                        <td className="py-3 px-4 text-emerald-600 font-bold">
                          {payment.amount?.toFixed(2)} {payment.currency}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs font-bold uppercase rounded-lg ${
                            payment.status === 'paid' 
                              ? 'bg-green-50 text-green-600' 
                              : payment.status === 'failed'
                              ? 'bg-red-50 text-red-500'
                              : 'bg-amber-50 text-amber-600'
                          }`}>
                            {payment.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={!!cancelConfirm} onOpenChange={(open) => { if (!open) setCancelConfirm(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{language === 'de' ? 'Buchung stornieren?' : 'Cancel Booking?'}</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 mt-2 text-sm">
            {language === 'de' 
              ? 'Möchten Sie diese Buchung wirklich stornieren? Diese Aktion kann nicht rückgängig gemacht werden.' 
              : 'Are you sure you want to cancel this booking? This action cannot be undone.'}
          </p>
          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={() => setCancelConfirm(null)} className="flex-1 border-slate-200 text-slate-600">
              {language === 'de' ? 'Zurück' : 'Back'}
            </Button>
            <Button onClick={() => cancelConfirm?.onConfirm()} className="flex-1 bg-red-500 text-white hover:bg-red-600 font-bold" data-testid="confirm-cancel-booking">
              {language === 'de' ? 'Ja, stornieren' : 'Yes, cancel'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Submit Match Result Dialog */}
      <Dialog open={!!resultDialog} onOpenChange={(o) => { if (!o) setResultDialog(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="submit-result-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-amber-700">
              🏆 {language === 'de' ? 'Sieger eingeben' : 'Submit result'}
            </DialogTitle>
            <DialogDescription>
              {resultDialog && resultDialog.match.match_kind === 'competitive'
                ? (language === 'de'
                    ? 'Wertungsspiel — wähle die 2 Sieger. Die Padlr-Ratings aller 4 Spieler werden aktualisiert.'
                    : 'Rated match — pick the 2 winners. Padlr ratings for all 4 players will update.')
                : (language === 'de'
                    ? 'Freundschaftsspiel — wähle die 2 Sieger. Wird nur zur Erinnerung gespeichert, kein Rating-Update.'
                    : 'Friendly match — pick the 2 winners. Saved for the record only, no rating update.')}
            </DialogDescription>
          </DialogHeader>
          {resultDialog && resultDialog.match.match_kind === 'competitive' && (
            <div className="my-2">
              <CompetitiveResultForm players={resultDialog.match.current_players || []} de={language === 'de'}
                onChange={(r) => setResultDialog(d => (d ? { ...d, comp: r } : d))} />
            </div>
          )}
          {resultDialog && resultDialog.match.match_kind !== 'competitive' && (
            <div className="space-y-2 my-2">
              {(resultDialog.match.current_players || []).map((p) => {
                const isSelected = resultDialog.winners.includes(p.id);
                const canToggle = isSelected || resultDialog.winners.length < 2;
                return (
                  <button
                    key={p.id}
                    onClick={() => {
                      if (!canToggle) return;
                      const newWinners = isSelected
                        ? resultDialog.winners.filter(w => w !== p.id)
                        : [...resultDialog.winners, p.id];
                      setResultDialog({ ...resultDialog, winners: newWinners });
                    }}
                    disabled={!canToggle}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border transition-all ${
                      isSelected
                        ? 'bg-emerald-50 border-emerald-400 text-emerald-800 font-semibold'
                        : canToggle
                        ? 'bg-white border-slate-200 text-slate-800 hover:border-emerald-300'
                        : 'bg-slate-50 border-slate-100 text-slate-400 cursor-not-allowed'
                    }`}
                    data-testid={`winner-toggle-${p.id}`}
                  >
                    <span>{isSelected && '🏆 '}{p.name}</span>
                    {isSelected && <Check size={18} weight="bold" className="text-emerald-600" />}
                  </button>
                );
              })}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResultDialog(null)} className="border-slate-200" data-testid="submit-result-cancel">
              {language === 'de' ? 'Abbrechen' : 'Cancel'}
            </Button>
            <Button
              onClick={async () => {
                const isComp = resultDialog?.match.match_kind === 'competitive';
                if (!resultDialog || (isComp ? !resultDialog.comp?.valid : resultDialog.winners.length !== 2)) return;
                setSubmittingResult(true);
                try {
                  await API.post(`/matches/${resultDialog.match.id}/result`, isComp
                    ? { winner_ids: resultDialog.comp.winner_ids, sets: resultDialog.comp.sets }
                    : { winner_ids: resultDialog.winners });
                  toast.success(language === 'de' ? 'Resultat gespeichert — Ratings aktualisiert' : 'Result saved — ratings updated');
                  setResultDialog(null);
                  setRatingRefreshKey(k => k + 1);  // trigger PadlrRating re-fetch
                  loadData();
                } catch (e) {
                  toast.error(e.response?.data?.detail || (language === 'de' ? 'Fehler' : 'Error'));
                } finally { setSubmittingResult(false); }
              }}
              disabled={!resultDialog || (resultDialog.match.match_kind === 'competitive' ? !resultDialog.comp?.valid : resultDialog.winners.length !== 2) || submittingResult}
              className="bg-amber-500 hover:bg-amber-600 text-white font-bold"
              data-testid="submit-result-confirm"
            >
              {submittingResult
                ? (language === 'de' ? 'Speichert…' : 'Saving…')
                : (language === 'de' ? 'Resultat speichern' : 'Save result')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
